
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { AdTypeObject } from '@/store/modules/Ads/AdTypesModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'ad-types-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('adTypesUpdating'), [
          translate('ads'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_AD_TYPE,
        route.params.id
      );

      const type = ref<AdTypeObject>(data.data);
      const icon = ref(type.value.icon);
      const webIcon = ref(type.value.webIcon);
      const previewImage = (e) => {
        const file = e.target.files[0];
        icon.value = URL.createObjectURL(file);
      };
      const previewWebImage = (e) => {
        const file = e.target.files[0];
        webIcon.value = URL.createObjectURL(file);
      };
      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const AdTypeSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        let logoFormData;
        let logoWebFormData;
        if (values.logo) {
          logoFormData = new FormData();
          logoFormData.append('images', values.logo);
          delete values.logo;
        }

        if (values.webIcon) {
          logoWebFormData = new FormData();
          logoWebFormData.append('images', values.webIcon);
          delete values.webIcon;
        }

        await store.dispatch(Actions.UPDATE_AD_TYPE, {
          id: type.value?.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          if (logoFormData) {
            await store.dispatch(Actions.UPLOAD_ADS_TYPE_IMAGE, {
              data: logoFormData,
              id: type.value?.id,
            });
          }
          if (logoWebFormData) {
            await store.dispatch(Actions.UPLOAD_ADS_TYPE_WEB_IMAGE, {
              data: logoWebFormData,
              id: type.value?.id,
            });
          }

          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_AD_TYPE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'ad-types-listing' });
          });
        }
      };
      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        icon,
        previewImage,
        previewWebImage,
        webIcon,
        onSubmitUpdate,
        submitButton,
        translate,
        AdTypeSchema,
        goBack,
        type,
        can,
      };
    },
  });
